import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { tokens } from "../../theme";
import {
    useTheme, Box, Container, Grid, Card, TextField, Button, Table, TableHead, TableRow, TableCell, TableBody, Typography, Paper,
    CardMedia, CardActions, Slider, IconButton
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

import EmailIcon from "@mui/icons-material/Email";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';

import 'dayjs/locale/en-gb';
import 'dayjs/locale/es-us';
import dayjs from 'dayjs';

import { tableCellClasses } from '@mui/material/TableCell';
import { styled } from '@mui/material/styles';
import TableContainer from '@mui/material/TableContainer';

import { __fetching_data__ } from '../../components/api.js';
import Preloader from '../../components/Preloader';
import './bidask.css'
import FontLoader from '../../components/FontLoader';
import StatBox from "../../components/StatBox";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

function createData(vol_bid, bid, ask, vol_ask) {
    return { vol_bid, bid, ask, vol_ask };
}

function createDataTickmatch(open, high, low, close) {
    return { open, high, low, close };
}

const formatTime = (time) => {
    if (typeof time === 'object') {
        return `${dayjs(time).hour()}:${String(dayjs(time).minute()).padStart(2, '0')}`;
    }
    return time;
};

const formatDate = (date) => {
    if (typeof date === 'object') {
        return `${dayjs(date).year()}-${String(dayjs(date).month() + 1).padStart(2, '0')}-${String(dayjs(date).date()).padStart(2, '0')}`;
    }
    return date;
};

const objectToQueryString = (obj) => {
    return Object.keys(obj)
        .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(obj[key]))
        .join('&');
}

const NumberWithCommasAndDecimal = (number, decimalPlaces) => {
    const formattedNumber = decimalPlaces === 2
        ? Number(number).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : Number(number).toLocaleString('en-US');
    return <div>{formattedNumber}</div>;
}

const hightLow = (values, key) => {
    var min = Math.min(...values.map(item => parseFloat(item[key])));
    var max = Math.max(...values.map(item => parseFloat(item[key])));
    return { max, min };
}

function createInitialRows(depth = 10) {
    const safeDepth = Math.max(10, Math.min(depth, 50));
    const rows = [];
    for (let i = 0; i < safeDepth; i++) {
        rows.push(createData(0, 0, 0, 0, 0, 0, 0, 0, 0, 0));
    }
    rows.push(createData("", "", "", ""));
    rows.push(createData("Sum Bid", "Sum 5 Bid", "Sum 5 Ask", "Sum Ask"));
    rows.push(createData(0, 0, 0, 0));
    return rows;
}

const initialInRange = [createDataTickmatch(0, 0, 0, 0)];
const initialActual = [createDataTickmatch(0, 0, 0, 0)];

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const skeletonCSS = `
@keyframes rpShimmer {
    0%   { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
}
`;

function Shimmer({ delay = 0 }) {
    return (
        <div style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'linear-gradient(90deg, transparent 0%, rgba(56,189,248,0.07) 40%, rgba(125,211,252,0.15) 50%, rgba(56,189,248,0.07) 60%, transparent 100%)',
            animation: `rpShimmer 1.8s ease-in-out ${delay}s infinite`,
        }} />
    );
}

function SkeletonBlock({ w = '100%', h = 10, radius = 4, delay = 0, style = {} }) {
    return (
        <div style={{
            width: w, height: h, borderRadius: radius,
            backgroundColor: '#1e293b',
            position: 'relative', overflow: 'hidden', flexShrink: 0,
            display: 'inline-block',
            ...style,
        }}>
            <Shimmer delay={delay} />
        </div>
    );
}

function SkeletonTableRow({ index }) {
    const bvW = 36 + (index % 4) * 8;
    const bW = 32 + (index % 3) * 6;
    const aW = 32 + ((index + 1) % 3) * 6;
    const avW = 36 + ((index + 2) % 4) * 8;
    const d = index * 0.04;
    return (
        <tr style={{ borderBottom: '1px solid #0f172a', backgroundColor: index % 2 === 0 ? 'rgba(15,23,42,0.4)' : 'transparent' }}>
            <td align="center" style={{ padding: 4 }}>
                <SkeletonBlock w={bvW} h={9} delay={d} />
            </td>
            <td align="center" style={{ padding: 4 }}>
                <SkeletonBlock w={bW} h={9} delay={d + 0.05} />
            </td>
            <td align="center" style={{ padding: 4 }}>
                <SkeletonBlock w={aW} h={9} delay={d + 0.05} />
            </td>
            <td align="center" style={{ padding: 4 }}>
                <SkeletonBlock w={avW} h={9} delay={d} />
            </td>
        </tr>
    );
}

function SkeletonStatSection({ delay = 0 }) {
    return (
        <div style={{ backgroundColor: '#111827', border: '1px solid #334155', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ backgroundColor: '#1e293b', padding: '8px 12px' }}>
                <SkeletonBlock w={60} h={9} delay={delay} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', padding: '8px 12px 4px' }}>
                {[0, 1, 2, 3].map(i => <SkeletonBlock key={i} w={28} h={7} delay={delay + i * 0.05} style={{ margin: 'auto' }} />)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', padding: '4px 12px 12px' }}>
                {[40, 48, 48, 40].map((w, i) => <SkeletonBlock key={i} w={w} h={12} delay={delay + 0.15 + i * 0.05} style={{ margin: 'auto' }} />)}
            </div>
        </div>
    );
}

/** Overlay covering table + stats — header & slider stay interactive */
function OrderBookSkeleton({ rowCount = 10 }) {
    return (
        <div style={{
            position: 'absolute', inset: 0, zIndex: 40,
            backgroundColor: 'rgba(11,17,26,0.95)',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
        }}>
            <style>{skeletonCSS}</style>

            {/* Table header */}
            <table className="w-full table-fixed" style={{ flexShrink: 0 }}>
                <thead>
                    <tr style={{ backgroundColor: '#0f172a', borderBottom: '1px solid #1e293b' }}>
                        {['Vol BID', 'BID', 'ASK', 'Vol ASK'].map((_, i) => (
                            <td key={i} align="center" style={{ padding: 6 }}>
                                <SkeletonBlock w={40} h={8} delay={i * 0.06} style={{ margin: 'auto' }} />
                            </td>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {[...Array(rowCount)].map((_, i) => <SkeletonTableRow key={i} index={i} />)}
                    {/* blank + sum + sum value rows */}
                    <tr style={{ height: 26, backgroundColor: '#111827', borderBottom: '1px solid #1e293b' }} />
                    <tr style={{ backgroundColor: '#111827', borderBottom: '1px solid #1e293b' }}>
                        <td align="center" style={{ padding: 4 }}><SkeletonBlock w={52} h={9} delay={0.2} style={{ margin: 'auto' }} /></td>
                        <td /><td />
                        <td align="center" style={{ padding: 4 }}><SkeletonBlock w={52} h={9} delay={0.2} style={{ margin: 'auto' }} /></td>
                    </tr>
                    <tr style={{ backgroundColor: '#111827' }}>
                        <td align="center" style={{ padding: 4 }}><SkeletonBlock w={48} h={9} delay={0.22} style={{ margin: 'auto' }} /></td>
                        <td /><td />
                        <td align="center" style={{ padding: 4 }}><SkeletonBlock w={48} h={9} delay={0.22} style={{ margin: 'auto' }} /></td>
                    </tr>
                </tbody>
            </table>

            {/* Stats */}
            <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16,
                padding: 16, backgroundColor: '#0f172a', borderTop: '1px solid #1e293b',
                marginTop: 'auto', flexShrink: 0,
            }}>
                <SkeletonStatSection delay={0} />
                <SkeletonStatSection delay={0.1} />
            </div>
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ReplayPanel(page, country) {

    const [startTime, setStartTime] = useState("00:00");
    const [endTime, setEndTime] = useState("00:00");
    const [sliderValue, setSliderValue] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(startTime);
    const [orderBook, setOrderBook] = useState([]);
    const [symbolFocused, setSymbolFocused] = useState(false);

    const totalBid = orderBook.reduce((sum, row) => sum + (row.bidVol || 0), 0);
    const totalAsk = orderBook.reduce((sum, row) => sum + (row.askVol || 0), 0);

    const toSeconds = (time) => {
        if (!time || !time.includes(":")) return 0;
        const [h, m] = time.split(":").map(Number);
        if (isNaN(h) || isNaN(m)) return 0;
        return h * 3600 + m * 60;
    };

    const toHHMMSS = (seconds) => {
        const h = String(Math.floor(seconds / 3600)).padStart(2, "0");
        const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
        const s = String(seconds % 60).padStart(2, "0");
        return `${h}:${m}:${s}`;
    };

    useEffect(() => {
        const startSec = toSeconds(startTime);
        const endSec = toSeconds(endTime);
        if (endSec <= startSec) { setCurrentTime("00:00:00"); return; }
        const total = endSec - startSec;
        const percent = Number(sliderValue) || 0;
        const current = startSec + (percent / 100) * total;
        setCurrentTime(toHHMMSS(Math.floor(current)));
    }, [sliderValue, startTime, endTime]);

    useEffect(() => {
        const startSec = toSeconds(startTime);
        const endSec = toSeconds(endTime);
        if (endSec <= startSec) return;
        const total = endSec - startSec;
        const percent = Number(sliderValue) || 0;
        const current = startSec + (percent / 100) * total;
        setCurrentTime(toHHMMSS(Math.floor(current)));
    }, [sliderValue, startTime, endTime]);

    const generateOrderBook = (timePercent) => {
        const basePrice = 72 - (timePercent * 0.02);
        const rows = [];
        for (let i = 0; i < 10; i++) {
            const bidPrice = (basePrice - i * 0.25).toFixed(2);
            const askPrice = (parseFloat(bidPrice) + 0.25).toFixed(2);
            rows.push({ bidVol: Math.floor(200000 + Math.random() * 400000), bid: bidPrice, ask: askPrice, askVol: Math.floor(200000 + Math.random() * 400000) });
        }
        return rows;
    };

    useEffect(() => { setOrderBook(generateOrderBook(0)); }, []);
    useEffect(() => { setOrderBook(generateOrderBook(sliderValue)); }, [sliderValue]);

    const class_page_name = '.change-tracking' + page + ' td';
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const csrfToken = useSelector((state) => state.csrfToken);
    const now = dayjs();
    const today = now.format('YYYY-MM-DD');
    const today_value = dayjs(today).subtract(0, 'day');

    const [stHour, stMinute] = [10, 0];
    const [enHour, enMinute] = [16, 30];
    const stEnTimeMin = dayjs().set('hour', stHour).set('minute', stMinute);
    const stEnTimeMax = dayjs().set('hour', enHour).set('minute', enMinute);

    const [depth, setDepth] = useState(10);
    const initialRow = createInitialRows(depth);

    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState(initialRow);
    const [rowsInRange, setRowsInRange] = useState(initialInRange);
    const [rowsActual, setRowsActual] = useState(initialActual);

    const [initialValues, setInitialValues] = useState({
        start_date: today_value,
        end_date: today_value,
        start_time: stEnTimeMin,
        end_time: stEnTimeMax,
        symbol: "",
        speed: 1,
    });

    const [countRecordBidAsk, setCountRecordBidAsk] = useState(0);
    const [bidaskLength, setBidAskLength] = useState(1);
    const [resultBidAsk, setResultBidAsk] = useState([]);
    const [resultTickMatch, setResultTickMatch] = useState([]);

    function transformOrderBookArrayV1(orderBooks, symbolOverride = "AAV") {
        const formatPrice = (p) => p.toFixed(2);
        const formatVolume = (v) => Math.round(v).toString();
        const safeDepth = Math.max(10, Math.min(depth, 50));
        return orderBooks.map((data) => {
            const result = {};
            const asks = Array.isArray(data.asks) ? [...data.asks] : [];
            const bids = Array.isArray(data.bids) ? [...data.bids] : [];
            while (asks.length < safeDepth) asks.push({ price: 0.0, volume: 0 });
            while (bids.length < safeDepth) bids.push({ price: 0.0, volume: 0 });
            asks.sort((a, b) => a.price - b.price).slice(0, safeDepth).forEach((ask, i) => { result[`Ask${i + 1}`] = formatPrice(ask.price); result[`VolAsk${i + 1}`] = formatVolume(ask.volume); });
            bids.sort((a, b) => b.price - a.price).slice(0, safeDepth).forEach((bid, i) => { result[`Bid${i + 1}`] = formatPrice(bid.price); result[`VolBid${i + 1}`] = formatVolume(bid.volume); });
            try { const iso = data.timestamp_th; const [dp, tp] = iso.split("T"); result["Time"] = `${dp} ${tp.split("+")[0].slice(0, 8)}`; } catch { result["Time"] = "Invalid Date"; }
            result["Symbol"] = symbolOverride;
            return result;
        });
    }

    const [offset, setOffset] = useState(0);
    const [limit] = useState(500);
    const [hasMore, setHasMore] = useState(true);

    function transformOrderBookArray(orderBooks, symbolOverride = "AAV") {
        const formatPrice = (p) => Number(p).toFixed(2);
        const formatVolume = (v) => Math.round(Number(v)).toString();
        const safeDepth = Math.max(10, Math.min(depth, 50));
        return orderBooks.map((data) => {
            const result = {};
            const asks = Array.isArray(data.asks) ? data.asks.map(([price, volume]) => ({ price, volume })) : [];
            const bids = Array.isArray(data.bids) ? data.bids.map(([price, volume]) => ({ price, volume })) : [];
            while (asks.length < safeDepth) asks.push({ price: 0.0, volume: 0 });
            while (bids.length < safeDepth) bids.push({ price: 0.0, volume: 0 });
            asks.sort((a, b) => a.price - b.price).slice(0, safeDepth).forEach((ask, i) => { result[`Ask${i + 1}`] = formatPrice(ask.price); result[`VolAsk${i + 1}`] = formatVolume(ask.volume); });
            bids.sort((a, b) => b.price - a.price).slice(0, safeDepth).forEach((bid, i) => { result[`Bid${i + 1}`] = formatPrice(bid.price); result[`VolBid${i + 1}`] = formatVolume(bid.volume); });
            try { const iso = data.timestamp_th; const [dp, tp] = iso.split("T"); result["Time"] = `${dp} ${tp.split("+")[0].slice(0, 8)}`; } catch { result["Time"] = "Invalid Date"; }
            result["Symbol"] = data.symbol || symbolOverride;
            return result;
        });
    }

    useEffect(() => {
        console.log(country);
        if (country == 'usa') { setDepth(Number(50)); } else { setDepth(Number(10)); }
    }, []);

    useEffect(() => { setRows(createInitialRows(depth)); }, [depth]);

    const resetProcess = () => {
        previousBidAsk.current = {};
        previousTickMatchInRange.current = {};
        previousTickMatchActual.current = {};
        document.querySelectorAll(class_page_name).forEach(cell => { cell.style.backgroundColor = ''; });
        setRows(initialRow);
        setRowsInRange(initialInRange);
        setRowsActual(initialActual);
        setCountRecordBidAsk(0);
        setIsPlaying(false);
        setTime2Show('00:00:00');
    };

    const handleSubmitForm = async () => {
        setLoading(true);
        resetProcess();
        setOffset(0);
        setHasMore(true);
        setResultBidAsk([]);
        await fetchData(0);
        setLoading(false);
        setIsPlaying(true);
    };

    const fetchData = async (currentOffset) => {
        const values = {
            ...initialValues,
            start_date: formatDate(initialValues.start_date),
            end_date: formatDate(initialValues.end_date),
            start_time: formatTime(initialValues.start_time),
            end_time: formatTime(initialValues.end_time),
            country, limit, offset: currentOffset,
        };
        const response = await __fetching_data__({
            url: `${window.config.base_api_url}/bidask_dejavu_report?${objectToQueryString(values)}`,
            method: "GET",
            headers: { "xsrf-token": csrfToken },
        });
        console.log(response);
        if (response.status) {
            let transformed = response.data.bidask;
            if (country === "usa") transformed = transformOrderBookArray(transformed, initialValues.symbol);
            setResultBidAsk(prev => [...prev, ...transformed]);
            if (country !== "usa") setResultTickMatch(prev => [...prev, ...response.data.tickmatch]);
            const totalRows = country === "usa" ? response.total.bidask : response.data.bidask.length;
            setBidAskLength(totalRows);
            if (country === "usa") {
                const nextOffset = currentOffset + limit;
                setOffset(nextOffset);
                if (nextOffset >= totalRows) { setHasMore(false); console.log("finish load more!"); }
                else { console.log("load more...."); fetchData(nextOffset); }
            }
        }
    };

    const handleSubmitForm2 = async () => {
        setLoading(true);
        resetProcess();
        const values = { ...initialValues, start_date: formatDate(initialValues.start_date), end_date: formatDate(initialValues.end_date), start_time: formatTime(initialValues.start_time), end_time: formatTime(initialValues.end_time), country };
        const response = await __fetching_data__({ url: `${window.config.base_api_url}/bidask_dejavu_report?${objectToQueryString(values)}`, method: "GET", headers: { "xsrf-token": csrfToken } });
        if (response.status) {
            let transformed = response.data.bidask;
            if (country == 'usa') transformed = transformOrderBookArray(transformed, initialValues.symbol);
            console.log(transformed);
            setLoading(false);
            if (country == 'usa') {
                setResultBidAsk(prev => [...prev, ...transformed]);
                setResultTickMatch(prev => [...prev, ...response.data.tickmatch]);
                setBidAskLength(prev => prev + transformed.length);
            } else {
                setResultBidAsk(transformed);
                setResultTickMatch(response.data.tickmatch);
                setBidAskLength(response.data.bidask.length);
            }
            setIsPlaying(true);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setInitialValues(prev => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (name, value) => {
        setInitialValues(prev => {
            let updated = { ...prev, [name]: value };
            if (name === 'start_date') {
                updated.end_date = value;
                if (dayjs(value).isSame(dayjs(), 'day')) {
                    updated.end_time = dayjs().isAfter(stEnTimeMax) ? stEnTimeMax.format("HH:mm") : dayjs().format("HH:mm");
                }
            }
            return updated;
        });
    };

    const handleTimeChange = (name, value) => {
        setInitialValues(prev => ({
            ...prev,
            [name]: `${String(dayjs(value).hour()).padStart(2, '0')}:${String(dayjs(value).minute()).padStart(2, '0')}`
        }));
    };

    useEffect(() => {
        if (dayjs(initialValues.start_date).isSame(dayjs(), 'day')) {
            const interval = setInterval(() => {
                const now = dayjs();
                const maxTime = dayjs().set('hour', 16).set('minute', 30);
                setInitialValues(prev => ({ ...prev, end_time: now.isAfter(maxTime) ? maxTime.format("HH:mm") : now.format("HH:mm") }));
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [initialValues.start_date]);

    const StyledTableCell = styled(TableCell)(({ theme }) => ({
        [`&.${tableCellClasses.head}`]: {
            fontSize: 14, backgroundColor: colors.blueAccent[500], color: theme.palette.common.white, fontWeight: 'bold', padding: '8px 8px', fontFamily: 'Lato, Sans-serif',
        },
        [`&.${tableCellClasses.body}`]: {
            fontSize: 14, backgroundColor: colors.blueAccent[500], color: theme.palette.common.white, fontWeight: 'bold', padding: '8px 8px', fontFamily: 'Lato, Sans-serif',
        },
    }));

    const handleClickPlayPause = () => setIsPlaying(!isPlaying);

    const currentIndexRef = useRef(0);
    const handleSliderChange = (event, newValue) => {
        console.log(newValue, bidaskLength, countRecordBidAsk);
        currentIndexRef.current = countRecordBidAsk;
        setCountRecordBidAsk(newValue);
        if (resultBidAsk[newValue - 1]) {
            setTime2Show(dayjs(resultBidAsk[newValue - 1].Time).format('HH:mm:ss'));
        }
    };

    const [Time2Show, setTime2Show] = useState('00:00:00');
    const previousBidAsk = useRef({});
    const previousTickMatchInRange = useRef({});
    const previousTickMatchActual = useRef({});
    const [speedChanged, setSpeedChanged] = useState(false);
    const prevSpeedRef = useRef(initialValues.speed);

    const extractAndConvertChildren = (obj) =>
        Object.keys(obj).reduce((acc, key) => {
            if (obj[key]) {
                if (typeof obj[key] === 'object' && obj[key].props && obj[key].props.children) {
                    acc[key] = parseFloat(obj[key].props.children.replace(/,/g, ''));
                } else { acc[key] = obj[key]; }
            }
            return acc;
        }, {});

    const compareData = (oldData, newData) => {
        const differences = [];
        oldData.forEach((oldRow, index) => {
            const newRow = newData[index];
            if (!newRow) return;
            const diff = {};
            for (const key in oldRow) {
                if (oldRow[key] !== newRow[key]) {
                    const oldVal = parseFloat(oldRow[key]) || 0;
                    const newVal = parseFloat(newRow[key]) || 0;
                    const change = newVal - oldVal;
                    diff[key] = { old: oldVal, new: newVal, change, type: change === 0 ? "" : change > 0 ? 'positive' : 'negative', rowIndex: index };
                }
            }
            if (Object.keys(diff).length > 0) differences.push({ index, diff });
        });
        return differences;
    };

    const isEmpty = (obj) => { for (let key in obj) if (obj.hasOwnProperty(key)) return false; return true; };

    useEffect(() => {
        let interval;
        let speed = initialValues.speed == 0 || initialValues.speed == null ? 1 : initialValues.speed;
        if (isPlaying) {
            console.log('start playing...');
            interval = setInterval(async () => {
                speed = initialValues.speed == 0 || initialValues.speed == null ? 1 : initialValues.speed;
                if (speedChanged) { setLoading(true); await delay(2000); setLoading(false); }
                let time_minute_search = 0;
                setCountRecordBidAsk(prevCount => {
                    if (prevCount >= bidaskLength - 1) { setIsPlaying(false); clearInterval(interval); return prevCount; }
                    let bidask_per_sec = resultBidAsk[prevCount];
                    if (!bidask_per_sec) { alert("รอข้อมูลก่อน..."); return currentIndexRef.current; }

                    time_minute_search = dayjs(bidask_per_sec['Time']).format('YYYY-MM-DD HH:mm');
                    setTime2Show(dayjs(bidask_per_sec['Time']).format('HH:mm:ss'));

                    const isBefore = dayjs(formatDate(initialValues.start_date)).isBefore(dayjs('2023-05-08'));
                    document.querySelectorAll(class_page_name).forEach(cell => { cell.style.backgroundColor = ''; });

                    let currentBidAsk = [];
                    const updatedRows = [...rows];
                    if (isBefore) {
                        if (bidask_per_sec != undefined) {
                            currentBidAsk[0] = createData(NumberWithCommasAndDecimal(bidask_per_sec.VolBid1), NumberWithCommasAndDecimal(bidask_per_sec.Bid1, 2), NumberWithCommasAndDecimal(bidask_per_sec.Ask5, 2), NumberWithCommasAndDecimal(bidask_per_sec.VolAsk5));
                            currentBidAsk[1] = createData(NumberWithCommasAndDecimal(bidask_per_sec.VolBid2), NumberWithCommasAndDecimal(bidask_per_sec.Bid2, 2), NumberWithCommasAndDecimal(bidask_per_sec.Ask4, 2), NumberWithCommasAndDecimal(bidask_per_sec.VolAsk4));
                            currentBidAsk[2] = createData(NumberWithCommasAndDecimal(bidask_per_sec.VolBid3), NumberWithCommasAndDecimal(bidask_per_sec.Bid3, 2), NumberWithCommasAndDecimal(bidask_per_sec.Ask3, 2), NumberWithCommasAndDecimal(bidask_per_sec.VolAsk3));
                            currentBidAsk[3] = createData(NumberWithCommasAndDecimal(bidask_per_sec.VolBid4), NumberWithCommasAndDecimal(bidask_per_sec.Bid4, 2), NumberWithCommasAndDecimal(bidask_per_sec.Ask2, 2), NumberWithCommasAndDecimal(bidask_per_sec.VolAsk2));
                            currentBidAsk[4] = createData(NumberWithCommasAndDecimal(bidask_per_sec.VolBid5), NumberWithCommasAndDecimal(bidask_per_sec.Bid5, 2), NumberWithCommasAndDecimal(bidask_per_sec.Ask1, 2), NumberWithCommasAndDecimal(bidask_per_sec.VolAsk1));
                            currentBidAsk[12] = createData(
                                NumberWithCommasAndDecimal(parseFloat(bidask_per_sec.VolBid1) + parseFloat(bidask_per_sec.VolBid2) + parseFloat(bidask_per_sec.VolBid3) + parseFloat(bidask_per_sec.VolBid4) + parseFloat(bidask_per_sec.VolBid5)),
                                "", "",
                                NumberWithCommasAndDecimal(parseFloat(bidask_per_sec.VolAsk1) + parseFloat(bidask_per_sec.VolAsk2) + parseFloat(bidask_per_sec.VolAsk3) + parseFloat(bidask_per_sec.VolAsk4) + parseFloat(bidask_per_sec.VolAsk5)),
                            );
                            const extractedRows = currentBidAsk.map(extractAndConvertChildren);
                            if (!isEmpty(previousBidAsk.current)) {
                                compareData(previousBidAsk.current, extractedRows).map((obj) => {
                                    Object.entries(obj.diff).map(([key, value]) => {
                                        const cell = document.querySelector(`#cell-${value.rowIndex}-${key}`);
                                        if (cell) { cell.style.backgroundColor = value.type === 'positive' ? 'lightgreen' : 'lightcoral'; cell.style.color = 'black'; setTimeout(() => { cell.style.color = 'white'; }, 1000); }
                                    });
                                });
                            }
                            updatedRows[0] = currentBidAsk[0]; updatedRows[1] = currentBidAsk[1]; updatedRows[2] = currentBidAsk[2]; updatedRows[3] = currentBidAsk[3]; updatedRows[4] = currentBidAsk[4]; updatedRows[12] = currentBidAsk[12];
                            previousBidAsk.current = extractedRows;
                        }
                    } else {
                        for (let i = 1; i <= depth; i++) {
                            currentBidAsk[i - 1] = createData(
                                NumberWithCommasAndDecimal(bidask_per_sec[`VolBid${i}`]),
                                NumberWithCommasAndDecimal(bidask_per_sec[`Bid${i}`], 2),
                                NumberWithCommasAndDecimal(bidask_per_sec[`Ask${depth - i + 1}`], 2),
                                NumberWithCommasAndDecimal(bidask_per_sec[`VolAsk${depth - i + 1}`])
                            );
                        }
                        let sumBid = 0, sumAsk = 0, sum5Bid = 0, sum5Ask = 0;
                        for (let i = 1; i <= depth; i++) {
                            sumBid += parseFloat(bidask_per_sec[`VolBid${i}`] || 0);
                            sumAsk += parseFloat(bidask_per_sec[`VolAsk${i}`] || 0);
                            if (i <= 5) {
                                sum5Bid += parseFloat(bidask_per_sec[`VolBid${i}`] || 0);
                                sum5Ask += parseFloat(bidask_per_sec[`VolAsk${i}`] || 0);
                            }
                        }
                        currentBidAsk[depth + 2] = createData(NumberWithCommasAndDecimal(sumBid),
                            NumberWithCommasAndDecimal(sum5Bid),
                            NumberWithCommasAndDecimal(sum5Ask),
                            NumberWithCommasAndDecimal(sumAsk));
                        const extractedRows = currentBidAsk.map(extractAndConvertChildren);
                        if (!isEmpty(previousBidAsk.current)) {
                            compareData(previousBidAsk.current, extractedRows).forEach(obj => {
                                Object.entries(obj.diff).forEach(([key, value]) => {
                                    const cell = document.querySelector(`#cell-${page}-${value.rowIndex}-${key}`);
                                    if (cell) {
                                        // cell.style.backgroundColor = value.type === "positive" ? "lightgreen" : "lightcoral";
                                        // cell.style.color = "black";
                                        cell.style.color = value.type === "positive" ? "lightgreen" : "lightcoral";
                                        cell.style.fontWeight = 'bold';
                                        setTimeout(() => {
                                            cell.style.color = "white";
                                            cell.style.fontWeight = 'normal';
                                        }, 1000);
                                    }
                                });
                            });
                        }
                        for (let i = 0; i < depth; i++) {
                            updatedRows[i] = currentBidAsk[i];
                        }
                        updatedRows[depth + 2] = currentBidAsk[depth + 2];
                        previousBidAsk.current = extractedRows;
                    }
                    setRows(updatedRows);

                    if (resultTickMatch != undefined) {
                        let tickmatch_hight_low;
                        const tickmatch_per_min = resultTickMatch.filter(x => x.DtMinOfRec.substring(0, x.DtMinOfRec.length - 3) === time_minute_search);
                        const tickmatch_in_range = resultTickMatch.filter(x => {
                            const x1 = dayjs(x.DtMinOfRec), x21 = dayjs(formatDate(initialValues.start_date) + " " + formatTime(initialValues.start_time)), x22 = dayjs(formatDate(initialValues.end_date) + " " + formatTime(initialValues.end_time));
                            return x1 >= x21 && x1 <= x22;
                        });
                        if (tickmatch_per_min.length > 0) tickmatch_hight_low = hightLow(tickmatch_per_min, 'Last');
                        const updatedRowsInRange = [...rowsInRange];
                        const InRagneTickmatch = createDataTickmatch(
                            tickmatch_in_range.length > 0 ? NumberWithCommasAndDecimal(tickmatch_in_range[0].Last, 2) : 0,
                            tickmatch_hight_low?.max ?? previousTickMatchInRange?.current?.high ?? 0,
                            tickmatch_hight_low?.min ?? previousTickMatchInRange?.current?.low ?? 0,
                            tickmatch_in_range.length > 0 ? NumberWithCommasAndDecimal(tickmatch_in_range[tickmatch_in_range.length - 1].Last, 2) : 0
                        );
                        updatedRowsInRange[0] = InRagneTickmatch;
                        previousTickMatchInRange.current = {
                            open: InRagneTickmatch.open && InRagneTickmatch.open.props ? parseFloat(InRagneTickmatch.open.props.children) : 0,
                            high: parseFloat(InRagneTickmatch.high),
                            low: parseFloat(InRagneTickmatch.low),
                            close: InRagneTickmatch.close && InRagneTickmatch.close.props ? parseFloat(InRagneTickmatch.close.props.children) : 0,
                        };
                        setRowsInRange(updatedRowsInRange);

                        const updatedRowsActual = [...rowsActual];
                        const allHL = hightLow(resultTickMatch, 'Last');
                        const ActualTickmatch = createDataTickmatch(
                            resultTickMatch.length > 0 ? NumberWithCommasAndDecimal(resultTickMatch[0].Last, 2) : 0,
                            allHL?.max ?? previousTickMatchActual?.current?.high ?? 0,
                            allHL?.min ?? previousTickMatchActual?.current?.low ?? 0,
                            resultTickMatch.length > 0 ? NumberWithCommasAndDecimal(resultTickMatch[resultTickMatch.length - 1].Last, 2) : 0
                        );
                        updatedRowsActual[0] = ActualTickmatch;
                        previousTickMatchActual.current = {
                            open: ActualTickmatch.open && ActualTickmatch.open.props ? parseFloat(ActualTickmatch.open.props.children) : 0,
                            high: ActualTickmatch.high ? parseFloat(ActualTickmatch.high) : 0,
                            low: ActualTickmatch.low ? parseFloat(ActualTickmatch.low) : 0,
                            close: ActualTickmatch.close && ActualTickmatch.close.props ? parseFloat(ActualTickmatch.close.props.children) : 0,
                        };
                        setRowsActual(updatedRowsActual);
                    }

                    if (speed !== prevSpeedRef.current) setSpeedChanged(true); else setSpeedChanged(false);
                    prevSpeedRef.current = speed;
                    return prevCount + 1;
                });
            }, 1000 / speed);
        }
        return () => clearInterval(interval);
    }, [isPlaying, countRecordBidAsk, initialValues.speed]);

    // ─── Render ──────────────────────────────────────────────────────────────

    return (
        <div className="bg-[#111827] border border-slate-700 rounded-xl overflow-hidden">

            {/* HEADER (hidden alt) */}
            <div style={{ display: 'none' }} className="p-4 border-b border-slate-700 bg-[#0f172a]">
                {/* ... ส่วนนี้ซ่อนอยู่แล้ว ไม่ได้เปลี่ยนแปลง ... */}
            </div>

            {/* HEADER (active MUI form) */}
            <div className="p-4 border-b border-slate-700 bg-[#0f172a]">
                <Grid style={{ marginTop: '5px' }} display="flex" container spacing={2}>
                    <Grid sm={4} item>
                        {/* <TextField fullWidth id="symbol"
                            name="symbol" label="Symbol"
                            variant="outlined" required size='small'
                            value={initialValues.symbol} 
                            onChange={handleChange} /> */}
                        <TextField
                            fullWidth
                            id="symbol"
                            name="symbol"
                            label={initialValues.symbol === '' && !symbolFocused ? "Symbol" : ""}
                            variant="outlined"
                            required
                            size='small'
                            value={initialValues.symbol}
                            onChange={handleChange}
                            onFocus={() => setSymbolFocused(true)}
                            onBlur={() => setSymbolFocused(false)}
                        />
                    </Grid>
                    <Grid sm={4} item>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                            <DatePicker id="start_date" name="start_date" label="Start Date" slotProps={{ textField: { size: 'small' } }} value={initialValues.start_date} maxDate={today_value} onChange={(v) => handleDateChange('start_date', v)} />
                        </LocalizationProvider>
                    </Grid>
                    <Grid sm={4} item>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                            <DatePicker id="end_date" name="end_date" label="End Date" slotProps={{ textField: { size: 'small' } }} value={initialValues.end_date} maxDate={today_value} onChange={(v) => handleDateChange('end_date', v)} disabled />
                        </LocalizationProvider>
                    </Grid>
                </Grid>
                <Grid display="flex" container spacing={2} marginTop="2px">
                    <Grid sm={4} item>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                            <TimePicker id="start_time" name="start_time" label="Start Time" slotProps={{ textField: { size: 'small' } }} defaultValue={initialValues.start_time} ampm={false} minTime={stEnTimeMin.subtract(1, 'minute')} maxTime={stEnTimeMax.add(1, 'minute')} onChange={(v) => handleTimeChange('start_time', v)} />
                        </LocalizationProvider>
                    </Grid>
                    <Grid sm={4} item>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                            <TimePicker id="end_time" name="end_time" label="End Time" slotProps={{ textField: { size: 'small' } }} value={dayjs(initialValues.end_time, "HH:mm")} ampm={false} minTime={stEnTimeMin.subtract(1, 'minute')} maxTime={stEnTimeMax.add(1, 'minute')} onChange={(v) => handleTimeChange('end_time', v)} disabled={dayjs(initialValues.start_date).isSame(dayjs(), 'day')} />
                        </LocalizationProvider>
                    </Grid>
                    <Grid sm={4} item display="flex">
                        <TextField fullWidth id="speed" name="speed" label="Speed" variant="outlined" size='small' value={initialValues.speed} onChange={handleChange} type='number' />
                        <button style={{ marginLeft: '10px' }} onClick={handleSubmitForm} className="w-full bg-indigo-600 hover:bg-indigo-500 rounded-md text-sm font-semibold text-white transition">
                            SEARCH
                        </button>
                    </Grid>
                </Grid>

                {/* thin loading bar ใต้ form — แทน CircularProgress */}
                {loading && (
                    <div style={{ marginTop: 10, height: 2, borderRadius: 9999, backgroundColor: '#1e293b', overflow: 'hidden', position: 'relative' }}>
                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, transparent, #38bdf8, #7dd3fc, #38bdf8, transparent)', animation: 'rpShimmer 1.2s ease-in-out infinite' }} />
                        <style>{skeletonCSS}</style>
                    </div>
                )}

                <div className="mt-3 bg-black text-yellow-400 font-mono text-center py-2 rounded">
                    {Time2Show}
                </div>
            </div>

            {/* SLIDER */}
            <Grid marginTop="20px" marginBottom="10px">
                <Box display="flex" marginLeft="10px">
                    <Slider
                        defaultValue={1} aria-label="Time Slider" valueLabelDisplay="auto"
                        min={1} max={bidaskLength} value={countRecordBidAsk}
                        onChange={handleSliderChange}
                        valueLabelFormat={() => `${Time2Show}`}
                        sx={{ flex: 1, mr: 1 }}
                    />
                    <IconButton onClick={handleClickPlayPause}>
                        {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                    </IconButton>
                </Box>
            </Grid>

            {/* ORDER BOOK + STATS — skeleton overlay when loading */}
            <div style={{ position: 'relative' }}>
                {loading && <OrderBookSkeleton rowCount={depth} />}

                <div className="bg-[#0b111a]">
                    <table className="w-full table-fixed">
                        <thead>
                            <td align='center'>Vol BID</td>
                            <td align='center'>BID</td>
                            <td align='center'>ASK</td>
                            <td align='center'>Vol ASK</td>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => {
                                var row12 = rows[12]
                                if (index == 12 && row12.bid != 0) {
                                    var numBid = parseInt(rows[12].vol_bid.props.children.replace(',', ''))
                                    var numAsk = parseInt(rows[12].vol_ask.props.children.replace(',', ''))
                                    var num5Bid = parseInt(rows[12].bid.props.children.replace(',', ''))
                                    var num5Ask = parseInt(rows[12].ask.props.children.replace(',', ''))
                                    var colorText = numBid < numAsk ? 'lightgreen' : 'lightcoral';
                                    var color5Text = num5Bid < num5Ask ? 'lightgreen' : 'lightcoral';
                                    return <tr key={index}>
                                        <td align="center" style={{ padding: "4px", color: colorText }}>{row.vol_bid}</td>
                                        <td align="center" style={{ padding: "4px", color: color5Text }}>{row.bid}</td>
                                        <td align="center" style={{ padding: "4px", color: color5Text }}>{row.ask}</td>
                                        <td align="center" style={{ padding: "4px", color: colorText }}>{row.vol_ask}</td>
                                    </tr>
                                } else {
                                    return <tr key={index}>
                                        <td id={`cell-${page}-${index}-vol_bid`} align="center" style={{ padding: "4px" }}>{row.vol_bid}</td>
                                        <td id={`cell-${page}-${index}-bid`} align="center" style={{ padding: "4px" }}>{row.bid}</td>
                                        <td id={`cell-${page}-${index}-ask`} align="center" style={{ padding: "4px" }}>{row.ask}</td>
                                        <td id={`cell-${page}-${index}-vol_ask`} align="center" style={{ padding: "4px" }}>{row.vol_ask}</td>
                                    </tr>
                                }
                            })}
                        </tbody>
                    </table>
                </div>
                <Grid container display="none">
                    <Grid sm={6}>
                        <Grid item>
                            <Typography variant='h4'>In Range</Typography>
                        </Grid>
                        <Grid item>
                            <TableContainer component={Paper}>
                                <Table aria-label="simple table">
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell align="center">OPEN</StyledTableCell>
                                            <StyledTableCell align="center">HIGH</StyledTableCell>
                                            <StyledTableCell align="center">LOW</StyledTableCell>
                                            <StyledTableCell align="center">CLOSE</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rowsInRange.map((row, index) => {
                                            return (
                                                <TableRow key={index} sx={{ backgroundColor: index % 2 === 0 ? theme.palette.table.odd : theme.palette.table.even, }}>
                                                    <TableCell align="center" style={{ padding: '4px' }}>{row.open}</TableCell>
                                                    <TableCell align="center" style={{ padding: '4px' }}>{row.high}</TableCell>
                                                    <TableCell align="center" style={{ padding: '4px' }}>{row.low}</TableCell>
                                                    <TableCell align="center" style={{ padding: '4px' }}>{row.close}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                    <Grid sm={6} paddingLeft="10px">
                        <Grid item>
                            <Typography variant='h4'>Actual</Typography>
                        </Grid>
                        <Grid item>
                            <TableContainer component={Paper}>
                                <Table aria-label="simple table">
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell align="center">OPEN</StyledTableCell>
                                            <StyledTableCell align="center">HIGH</StyledTableCell>
                                            <StyledTableCell align="center">LOW</StyledTableCell>
                                            <StyledTableCell align="center">CLOSE</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rowsActual.map((row, index) => {
                                            return (
                                                <TableRow key={index} sx={{ backgroundColor: index % 2 === 0 ? theme.palette.table.odd : theme.palette.table.even, }}>
                                                    <TableCell align="center" style={{ padding: '4px' }}>{row.open}</TableCell>
                                                    <TableCell align="center" style={{ padding: '4px' }}>{row.high}</TableCell>
                                                    <TableCell align="center" style={{ padding: '4px' }}>{row.low}</TableCell>
                                                    <TableCell align="center" style={{ padding: '4px' }}>{row.close}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>


                </Grid>

                <div className="grid grid-cols-2 gap-4 p-4 border-t border-slate-700 bg-[#0f172a]">
                    <StatSection title="In Range" dataRow={rowsInRange} />
                    <StatSection title="Actual" dataRow={rowsActual} />
                </div>
            </div>

        </div>
    );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function OrderRow({ index, page, bidVol, bid, ask, askVol }) {
    const bidWidth = 100, askWidth = 100;
    return (
        <div className="grid grid-cols-4 items-center text-[12px] h-[32px] border-b border-slate-800 relative">
            <div className="relative text-right pr-3 text-slate-300 font-medium overflow-hidden">
                <div className="absolute right-0 top-0 h-full bg-blue-900/60" style={{ width: `${bidWidth}%` }} />
                <span className="relative z-10">{bidVol}</span>
            </div>
            <div className="text-center text-green-400 font-semibold">{bid}</div>
            <div className="text-center text-red-400 font-semibold">{ask}</div>
            <div className="relative text-left pl-3 text-slate-300 font-medium overflow-hidden">
                <div className="absolute left-0 top-0 h-full bg-red-900/60" style={{ width: `${askWidth}%` }} />
                <span className="relative z-10">{askVol}</span>
            </div>
        </div>
    );
}

function StatSection({ title, dataRow }) {
    return (
        <div className="bg-[#111827] border border-slate-700 rounded">
            <div className="px-3 py-2 text-[11px] text-slate-400 border-b border-slate-700 bg-[#1e293b]">{title}</div>
            <div className="grid grid-cols-4 text-[10px] text-slate-500 px-3 pt-2">
                <span>OPEN</span><span>HIGH</span><span>LOW</span><span>CLOSE</span>
            </div>
            {dataRow.map((row, index) => (
                <div key={index} className="grid grid-cols-4 px-3 pb-3 pt-1 text-[13px] font-semibold text-white">
                    <span>{row.open}</span><span>{row.high}</span><span>{row.low}</span><span>{row.close}</span>
                </div>
            ))}
        </div>
    );
}